import type { CustomAnswer, Field, QuestionDestination } from './types';

export interface QuestionSection { id: string | null; fields: Field[] }
export const supportsRouting = (field: Field) => field.type === 'choice' || field.type === 'dropdown';
export const hasRouting = (fields: Field[]) => fields.some(f => (supportsRouting(f) && f.routing?.enabled) || f.type === 'section');
export function questionSections(fields: Field[]): QuestionSection[] {
  const sections: QuestionSection[] = [{ id: null, fields: [] }];
  for (const field of fields) {
    if (field.type === 'section') sections.push({ id: field.id, fields: [] });
    sections.at(-1)!.fields.push(field);
  }
  return sections.filter(section => section.fields.length);
}
/** A page of questions' name in the registration steps: its section's short name, or else its title's first line, or Questions. */
export function sectionStepName(section: QuestionSection): string {
  const header = section.fields[0];
  return (header?.type === 'section' ? header.shortName?.trim() || header.label.trim().split('\n')[0] : '') || 'Questions';
}
export function questionPath(fields: Field[], answers: Record<string, CustomAnswer>): QuestionSection[] {
  const sections = questionSections(fields);
  if (!hasRouting(fields)) return [{ id: null, fields }];
  const path: QuestionSection[] = [];
  let index = 0;
  while (index < sections.length) {
    const section = sections[index]; path.push(section);
    let destination: QuestionDestination = section.fields[0]?.type === 'section' ? section.fields[0].sectionDestination ?? 'next' : 'next';
    for (const field of section.fields) {
      const answer = answers[field.id];
      if (!supportsRouting(field) || !field.routing?.enabled || typeof answer !== 'string' || !answer.trim()) continue;
      const option = (field.options ?? []).indexOf(answer);
      destination = option >= 0 ? field.routing.targets[option] ?? 'next' : field.allowOther ? field.routing.other ?? 'next' : 'next';
    }
    if (destination === 'end') break;
    const target = typeof destination === 'object' ? sections.findIndex(section => section.id === destination.sectionId) : index + 1;
    // Invalid/stale draft targets cannot trap a participant in a loop.
    index = target > index ? target : index + 1;
  }
  return path;
}
export const activeQuestionFields = (fields: Field[], answers: Record<string, CustomAnswer>) => questionPath(fields, answers).flatMap(section => section.fields);
export function activeQuestionAnswers(fields: Field[], answers: Record<string, CustomAnswer>): Record<string, CustomAnswer> {
  const ids = new Set(activeQuestionFields(fields, answers).map(field => field.id));
  return Object.fromEntries(Object.entries(answers).filter(([id]) => ids.has(id)));
}

/** Fields visited on every possible valid answer path, without enumerating combinations.
 * All edges move forward, as in questionPath. Optional routing also keeps the default exit.
 */
export function universallyActiveQuestionIds(fields: Field[]): Set<string> {
  const sections = questionSections(fields);
  const guaranteed: Set<string>[] = Array.from({ length: sections.length + 1 }, () => new Set());
  for (let index = sections.length - 1; index >= 0; index--) {
    const section = sections[index];
    const nextIndex = (destination: QuestionDestination) => {
      if (destination === 'end') return sections.length;
      const target = typeof destination === 'object' ? sections.findIndex(s => s.id === destination.sectionId) : index + 1;
      return target > index ? target : index + 1;
    };
    let exits = new Set([nextIndex(section.fields[0]?.type === 'section' ? section.fields[0].sectionDestination ?? 'next' : 'next')]);
    for (const field of section.fields) {
      if (!supportsRouting(field) || !field.routing?.enabled) continue;
      const destinations = (field.options ?? []).map((_, i) => nextIndex(field.routing!.targets[i] ?? 'next'));
      if (field.type === 'choice' && field.allowOther) destinations.push(nextIndex(field.routing.other ?? 'next'));
      exits = new Set([...(field.required && destinations.length ? [] : exits), ...destinations]);
    }
    const continuations = [...exits].map(exit => guaranteed[exit]);
    const intersection = new Set(continuations[0]);
    for (const continuation of continuations.slice(1)) for (const id of intersection) if (!continuation.has(id)) intersection.delete(id);
    guaranteed[index] = new Set([...section.fields.map(field => field.id), ...intersection]);
  }
  return guaranteed[0];
}
